<template>
  <Tag :value="label" :severity="severity" />
</template>

<script setup lang="ts">
/**
 * A document's lifecycle state as a chip — the one place the vocabulary is mapped.
 *
 * Shared because the reader and the library both show it, and a second copy of the
 * severity switch would be the first thing to drift.
 *
 * Tolerant reader, customer-facing edition: the backend types `lifecycle_state` as a
 * plain string precisely so a value added later reaches a deployed browser, and such
 * a value must degrade to a generic label — never to the raw enum string (a
 * server-sent identifier must not render to a customer), and never to a broken i18n
 * key path.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Tag from 'primevue/tag'

const props = defineProps<{ state?: string | null }>()

const { t } = useI18n()

const KNOWN_STATUSES = ['draft', 'final', 'archived']

const label = computed(() => {
  const state = props.state ?? 'draft'
  return KNOWN_STATUSES.includes(state)
    ? t(`authoredDocuments.status.${state}`)
    : t('authoredDocuments.status.other')
})

const severity = computed(() => {
  switch (props.state) {
    case 'final':
      return 'success'
    case 'archived':
      return 'secondary'
    default:
      return 'info'
  }
})
</script>
